import { type FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TournamentList } from '@/features/tournaments/components';
import { TournamentCardSkeleton } from '@/features/tournaments/components/TournamentCardSkeleton';
import { useAuth } from '@/hooks';
import { supabase } from '@/lib/api/supabase';
import type { Tournament } from '@/types';
import { dbToTournament } from '@/types';

/**
 * Event page showing tournaments
 */
export const EventPage: FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Tournaments state
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(true);
  const [tournamentsError, setTournamentsError] = useState<string | null>(null);

  // Fetch tournaments
  useEffect(() => {
    const fetchTournaments = async (): Promise<void> => {
      try {
        setTournamentsLoading(true);
        setTournamentsError(null);

        const { data, error: fetchError } = await supabase
          .from('tournaments')
          .select('*')
          .neq('status', 'draft')
          .eq('visible', true)
          .order('display_order', { ascending: true });

        if (fetchError) {
          throw fetchError;
        }

        if (data) {
          const tournaments = data.map(dbToTournament);
          setTournaments(tournaments);
        }
      } catch (err) {
        console.error('Failed to fetch tournaments:', err);
        setTournamentsError(err instanceof Error ? err.message : 'Failed to load tournaments');
      } finally {
        setTournamentsLoading(false);
      }
    };

    void fetchTournaments();
  }, []);

  const handleTournamentRegister = (tournamentId: string): void => {
    if (!user) {
      navigate('/login', { state: { returnTo: `/event-registration?tournament=${tournamentId}` } });
      return;
    }
    navigate('/event-registration', { state: { preselectedTournamentId: tournamentId } });
  };

  return (
    <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-viking text-white mb-2 drop-shadow-lg">Tournaments</h1>
          <p className="text-lg text-orange-200">
            Browse tournaments at Cactus Cup 2026
          </p>
        </div>

        {tournamentsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <TournamentCardSkeleton key={index} />
            ))}
          </div>
        ) : tournamentsError ? (
          <div className="text-center">
            <div className="rounded-lg bg-red-900/40 border border-red-500/40 p-6 text-red-200 backdrop-blur-md shadow-lg inline-block">
              <p className="font-semibold">Error loading tournaments</p>
              <p className="mt-2 text-sm">{tournamentsError}</p>
            </div>
          </div>
        ) : tournaments.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-white">No tournaments available at this time.</p>
          </div>
        ) : (
          <TournamentList tournaments={tournaments} onRegister={handleTournamentRegister} />
        )}
      </div>
    </div>
  );
};
